/**
 * R267i · Feb 2026 — Presets ▾ menu.
 *
 * Compact one-click switcher for saved table views. Sits beside the
 * columns menu inside AdvancedTable's toolbar. Reads from the same
 * `views` state used by SavedViewsBar so we never have two sources
 * of truth for named layouts.
 *
 * UX rules:
 *  - Split by scope: your presets first, workspace presets below.
 *  - Active preset gets a highlighted state.
 *  - `+ Save current as preset` opens a mini form that captures
 *    the current sort stack (including multi-sort), filters,
 *    column order/visibility and search term.
 *  - Preset name gets a `⋯ N sorts` badge when the layout carries
 *    more than one sort so power users see the shape at a glance.
 */
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Bookmark, ChevronDown, Star, User, ArrowUpDown, Trash2, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function PresetsMenu({ state, tableKey }) {
  const {
    views, activeViewId, currentLayout, dirty,
    applyView, saveNewView, deleteView, isAdmin,
  } = state;
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [form, setForm] = useState({ name: "", scope: "user" });
  const [saving, setSaving] = useState(false);

  const mine = views.filter((v) => v.scope === "user" && v.owner_user_id === user?.id);
  const wsShared = views.filter((v) => v.scope === "workspace");
  const activeView = views.find((v) => v.id === activeViewId);

  const doSave = async () => {
    if (!form.name.trim()) { toast.error("Preset needs a name"); return; }
    setSaving(true);
    try {
      const v = await saveNewView({
        name: form.name.trim(),
        description: "",
        scope: form.scope === "workspace" && isAdmin ? "workspace" : "user",
      });
      if (v) { setSaveOpen(false); setForm({ name: "", scope: "user" }); setOpen(false); }
    } finally { setSaving(false); }
  };

  const renderPreset = (v) => {
    const sortsCount = (v.config?.sort || []).length;
    const filtersCount = (v.config?.filters || []).length;
    const isActive = v.id === activeViewId;
    return (
      <div
        key={v.id}
        className={`flex items-start gap-2 px-2.5 py-1.5 text-sm hover:bg-muted cursor-pointer ${isActive ? "bg-primary/10" : ""}`}
        data-testid={`presets-item-${v.id}`}
        onClick={() => { applyView(v); setOpen(false); }}
      >
        <div className="w-4 pt-0.5">
          {isActive && <Check className="w-3 h-3 text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium truncate">{v.name}</span>
            {sortsCount > 1 && (
              <span className="text-[9px] font-mono px-1 py-[1px] bg-blue-500/15 text-blue-700 uppercase tracking-widest"
                     title={`Multi-sort · ${sortsCount} columns`}
                     data-testid={`presets-multisort-badge-${v.id}`}>
                ⋯ {sortsCount} sorts
              </span>
            )}
            {filtersCount > 0 && (
              <span className="text-[9px] font-mono px-1 py-[1px] bg-amber-500/15 text-amber-800 uppercase tracking-widest">
                {filtersCount}f
              </span>
            )}
          </div>
          {v.description && (
            <div className="text-[10px] text-muted-foreground truncate">{v.description}</div>
          )}
        </div>
        {v.scope !== "workspace" && v.owner_user_id === user?.id && (
          <button
            className="p-0.5 opacity-40 hover:opacity-100 hover:text-rose-600"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete preset "${v.name}"?`)) deleteView(v.id);
            }}
            title="Delete preset"
            data-testid={`presets-delete-${v.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1 h-8 px-2 border text-xs rounded-none ${
              activeView ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-muted"
            }`}
            data-testid={`table-presets-btn-${tableKey}`}
            title="Saved presets — layouts, sorts, filters"
          >
            <Bookmark className="w-3.5 h-3.5" />
            <span className="max-w-[120px] truncate">
              {activeView ? (dirty ? `${activeView.name} *` : activeView.name) : "Presets"}
            </span>
            <ChevronDown className="w-3 h-3 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-72" data-testid={`table-presets-menu-${tableKey}`}>
          <div className="max-h-[420px] overflow-y-auto">
            {mine.length > 0 && (
              <div className="pt-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2.5 py-1 flex items-center gap-1">
                  <User className="w-3 h-3" /> Your presets
                </div>
                {mine.map(renderPreset)}
              </div>
            )}
            {wsShared.length > 0 && (
              <div className="pt-1 border-t border-border">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2.5 py-1 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Workspace presets
                </div>
                {wsShared.map(renderPreset)}
              </div>
            )}
            {mine.length === 0 && wsShared.length === 0 && (
              <div className="px-2.5 py-3 text-[11px] text-muted-foreground italic text-center">
                No presets yet. Save one below to reuse this layout.
              </div>
            )}
          </div>
          <div className="border-t border-border p-1.5">
            <button
              type="button"
              onClick={() => { setSaveOpen(true); setOpen(false); }}
              className="w-full text-left text-xs px-2 py-1.5 hover:bg-muted flex items-center gap-1.5"
              data-testid={`table-presets-save-btn-${tableKey}`}
            >
              <ArrowUpDown className="w-3 h-3" />
              Save current as preset…
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm rounded-none" data-testid={`table-presets-save-dialog-${tableKey}`}>
          <DialogHeader>
            <DialogTitle>Save preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="label-eyebrow">Preset name</Label>
              <Input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Prospecting · Warm now"
                className="mt-1 h-9 rounded-none"
                data-testid={`table-presets-save-name-${tableKey}`}
              />
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  id={`preset-scope-${tableKey}`}
                  checked={form.scope === "workspace"}
                  onChange={(e) => setForm({ ...form, scope: e.target.checked ? "workspace" : "user" })}
                  data-testid={`table-presets-save-scope-${tableKey}`}
                />
                <label htmlFor={`preset-scope-${tableKey}`}>
                  Share with the whole workspace (admin)
                </label>
              </div>
            )}
            <div className="text-[11px] text-muted-foreground bg-muted/40 p-2">
              This preset will capture{" "}
              <span className="font-mono">{(currentLayout?.sort || []).length}</span> sort(s),{" "}
              <span className="font-mono">{(currentLayout?.filters || []).length}</span> filter(s), and
              your current column order.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={doSave} disabled={saving || !form.name.trim()} className="rounded-none"
                    data-testid={`table-presets-save-confirm-${tableKey}`}>
              {saving ? "Saving…" : "Save preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
